import { absoluteValue } from '@0xtorch/big-decimal'
import type {
  Action,
  CryptoCurrency,
  FiatCurrency,
  Transfer,
} from '@0xtorch/core'
import type { ParseToActions } from '../../types'
import { getAsset } from '../../utils/getAsset'
import type { Trade } from './schema'

export const parseSpotTradeHistoriesToActions: ParseToActions<Trade> = ({
  data,
  accountId,
  cryptoCurrencies,
  fiatCurrencies,
}): readonly Action[] => {
  // 時価総額降順で cryptoCurrencies をソート
  const sortedCryptoCurrencies = cryptoCurrencies.toSorted((a, b) => {
    if (a.market?.marketCapUsd === undefined) {
      return -1
    }
    if (b.market?.marketCapUsd === undefined) {
      return 1
    }
    return a.market.marketCapUsd > b.market.marketCapUsd ? -1 : 1
  })
  const actions: Action[] = []
  for (const item of data) {
    // symbol から base , quote asset を取得
    const [base, quote] = item.symbol.split('-')
    const baseAsset = getAsset({
      symbol: base,
      cryptoCurrencies: sortedCryptoCurrencies,
      fiatCurrencies,
    })
    if (baseAsset === undefined) {
      throw new Error(`asset not found: ${base}`)
    }
    const quoteAsset = getAsset({
      symbol: quote,
      cryptoCurrencies: sortedCryptoCurrencies,
      fiatCurrencies,
    })
    if (quoteAsset === undefined) {
      throw new Error(`asset not found: ${quote}`)
    }
    const feeAsset = getAsset({
      symbol: item.feeCurrency,
      cryptoCurrencies: sortedCryptoCurrencies,
      fiatCurrencies,
    })
    if (feeAsset === undefined) {
      throw new Error(`asset not found: ${item.feeCurrency}`)
    }
    // trade action 生成
    const action: Action = {
      type: 'trade',
      source: `kucoin_spot-trade_${item.tradeId}`,
      order: 0,
      comment: undefined,
      app: undefined,
      timestamp: item.createdAt,
      evidence: 'system-rule',
      transfers: [
        ...createTransfers(item, accountId, baseAsset, quoteAsset, feeAsset),
      ],
    }
    actions.push(action)
  }
  return actions
}

const createTransfers = (
  trade: Trade,
  accountId: string,
  base: CryptoCurrency | FiatCurrency,
  quote: CryptoCurrency | FiatCurrency,
  fee: CryptoCurrency | FiatCurrency,
): readonly Transfer[] => {
  switch (trade.side) {
    case 'buy': {
      // quote -> base
      return [
        // base
        {
          from: undefined,
          to: accountId,
          amount: trade.size,
          price: undefined,
          asset: base,
          direction: 'in',
        },
        // quote
        {
          from: accountId,
          to: undefined,
          amount: trade.funds,
          price: undefined,
          asset: quote,
          direction: 'out',
        },
        // fee
        ...(trade.fee.value === 0n
          ? []
          : [
              {
                from: accountId,
                to: undefined,
                amount: absoluteValue(trade.fee),
                price: undefined,
                asset: fee,
                direction: 'out' as const,
              },
            ]),
      ]
    }
    case 'sell': {
      // base -> quote
      return [
        // quote
        {
          from: undefined,
          to: accountId,
          amount: trade.funds,
          price: undefined,
          asset: quote,
          direction: 'in',
        },
        // base
        {
          from: accountId,
          to: undefined,
          amount: trade.size,
          price: undefined,
          asset: base,
          direction: 'out',
        },
        // fee
        ...(trade.fee.value === 0n
          ? []
          : [
              {
                from: accountId,
                to: undefined,
                amount: absoluteValue(trade.fee),
                price: undefined,
                asset: fee,
                direction: 'out' as const,
              },
            ]),
      ]
    }
    default: {
      throw new Error(`unknown trade side: ${trade.side satisfies never}`)
    }
  }
}
